import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiSearch, FiEye, FiEdit2, FiPrinter, FiPlus } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../config';
import AdminLayout from './AdminLayout';

const WarrantyList = () => {
  const navigate = useNavigate();
  const [warranties, setWarranties] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [registrationDate, setRegistrationDate] = useState('');
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');

  const fetchWarranties = useCallback(async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API_BASE_URL}/api/warranty-registrations`, config);
      
      let warrantiesData = [];
      
      // Handle different response formats
      if (Array.isArray(response.data)) {
        warrantiesData = response.data;
      } else if (response.data.warrantyRegistrations && Array.isArray(response.data.warrantyRegistrations)) {
        warrantiesData = response.data.warrantyRegistrations;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        warrantiesData = response.data.data;
      }
      
      setWarranties(warrantiesData);
    } catch (error) {
      console.error('Error fetching warranties:', error);
      toast.error('Failed to fetch warranties');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchWarranties();
  }, [token, navigate, fetchWarranties]);

  const handleSearch = async () => {
    if (!searchTerm.trim() && !purchaseDate && !registrationDate) {
      fetchWarranties();
      return;
    }

    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const params = new URLSearchParams();
      if (searchTerm.trim()) params.append('searchTerm', searchTerm.trim());
      if (purchaseDate) params.append('purchase_date', purchaseDate);
      if (registrationDate) params.append('registration_date', registrationDate);

      const response = await axios.get(
        `${API_BASE_URL}/api/warranty-registrations/search?${params.toString()}`,
        config
      );

      let searchData = [];
      if (Array.isArray(response.data)) {
        searchData = response.data;
      } else if (response.data.warrantyRegistrations && Array.isArray(response.data.warrantyRegistrations)) {
        searchData = response.data.warrantyRegistrations;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        searchData = response.data.data;
      }

      setWarranties(searchData);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed');
    }
  };

  const handleViewDetails = (id) => {
    navigate(`/admin/warranty/view/${id}`);
  };

  const handleCreateWarranty = () => {
    navigate('/admin/warranty/create');
  };

  const handleEditWarranty = (id) => {
    navigate(`/admin/warranty/edit/${id}`);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB');
  };

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="page-header">Warranty Registrations</h1>
            <p className="text-gray-500 mt-1 text-sm">View and manage all warranty claims</p>
          </div>
          <button
            onClick={handleCreateWarranty}
            className="btn-primary flex items-center gap-2"
          >
            <FiPlus size={18} />
            Create Warranty
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
          <div className="flex gap-4 items-end">
            <div className="flex-1 flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Search</label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Warranty SN, Customer Name, or Mobile"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="input-field pl-10"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Purchase Date</label>
              <input
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                className="input-field"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Registration Date</label>
              <input
                type="date"
                value={registrationDate}
                onChange={(e) => setRegistrationDate(e.target.value)}
                className="input-field"
              />
            </div>
            <div className="flex gap-2">
              <button onClick={handleSearch} className="btn-primary">
                Search
              </button>
              {(searchTerm || purchaseDate || registrationDate) && (
                <button
                  onClick={() => { setSearchTerm(''); setPurchaseDate(''); setRegistrationDate(''); fetchWarranties(); }}
                  className="btn-secondary"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Warranty Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : warranties.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No warranties found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Warranty SN
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mobile
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Model No
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Purchase Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Registration Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {warranties.map((warranty) => (
                    <tr key={warranty.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-semibold text-syakar-red">
                          {warranty.warranty_sn}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {warranty.customer_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {warranty.mobile}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {warranty.model_no || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {warranty.date_of_purchase ? formatDate(warranty.date_of_purchase) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {formatDate(warranty.registration_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleViewDetails(warranty.id)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title="View Details"
                          >
                            <FiEye size={18} />
                          </button>
                          <button
                            onClick={() => handleEditWarranty(warranty.id)}
                            className="text-green-600 hover:text-green-800 transition-colors"
                            title="Edit"
                          >
                            <FiEdit2 size={18} />
                          </button>
                          <button
                            onClick={() => navigate(`/admin/warranty/print/${warranty.id}`)}
                            className="text-gray-500 hover:text-gray-800 transition-colors"
                            title="Print"
                          >
                            <FiPrinter size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

    </AdminLayout>
  );
};

export default WarrantyList;